import { Icon } from '../../design-system/icons';

/**
 * Card seleccionable del cuestionario de onboarding (imagen + etiqueta + badge).
 * Compartida por los pasos del "Account setup" (single o multi-select).
 * El estado seleccionado usa `Stroke/Inverse` (borde 1px + inset shadow, sin salto
 * de ancho) y el badge `solid/circle-check` entra con un fade + scale sutil.
 * Ver ADR-710/712.
 */
export interface SelectableCardProps {
  label: string;
  image: string;
  selected: boolean;
  onClick: () => void;
  /** Semántica del grupo: 'radio' (single-select) o 'checkbox' (multi-select). */
  role?: 'radio' | 'checkbox';
  /**
   * Orientación de la card:
   * - `vertical` (default): imagen cuadrada arriba + label debajo (pasos 1 y 2, grid de 2 columnas).
   * - `horizontal`: imagen 112×112 a la izquierda + label a la derecha (paso 3, lista vertical).
   */
  orientation?: 'vertical' | 'horizontal';
}

export function SelectableCard({
  label,
  image,
  selected,
  onClick,
  role = 'radio',
  orientation = 'vertical',
}: SelectableCardProps) {
  const horizontal = orientation === 'horizontal';
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      onClick={onClick}
      className={`relative flex rounded-[16px] border bg-[#FAFAFA] p-4 text-center outline-none transition-[border-color,box-shadow] duration-200 ease-out focus-visible:ring-2 focus-visible:ring-[rgb(var(--brand-primary-rgb)_/_0.4)] focus-visible:ring-offset-2 ${
        horizontal ? 'items-center gap-5' : 'flex-col items-center gap-3'
      } ${
        selected
          ? 'border-[#18181B] shadow-[inset_0_0_0_1px_#18181B]' // Stroke/Inverse
          : 'border-[#D4D4D8] hover:border-[#9F9FA9]'
      }`}
    >
      {horizontal ? (
        // Imagen (cutout transparente) 112×112 sin frame, tal cual el DS.
        <img src={image} alt="" draggable={false} className="size-28 shrink-0 object-contain" />
      ) : (
        <div className="aspect-square w-full overflow-hidden rounded-[12px]">
          <img src={image} alt="" draggable={false} className="size-full object-cover" />
        </div>
      )}
      <span
        className={`flex h-9 items-center justify-center text-[14px] font-[600] leading-[18px] text-[#18181B] ${
          horizontal ? 'flex-1' : ''
        }`}
      >
        {label}
      </span>
      {/* Badge: aparece con un fade + scale sutil (transición suave). */}
      <span
        className={`pointer-events-none absolute right-2.5 top-2.5 transition-all duration-200 ease-out ${
          selected ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
        }`}
      >
        {/* solid/circle-check del DS: el check queda calado; el círculo blanco de
            atrás lo hace ver blanco sobre el borde oscuro. */}
        <span className="relative block size-6">
          <span className="absolute inset-[1px] rounded-full bg-white" />
          <Icon name="solid/circle-check" size={24} className="relative block text-[#18181B]" />
        </span>
      </span>
    </button>
  );
}

export default SelectableCard;
